<template>
  <Teleport to="body">
    <transition name="slide-up">
      <div
        v-if="isOpen"
        class="drawer-overlay"
        @click="closeDrawer">
        <div
          class="drawer-content"
          :class="{
            'drawer-content--no-handle': hideHandle,
            'drawer-content--dragging': isDragging,
          }"
          :style="contentStyle"
          @click.stop>
          <div
            v-if="isMobile && !hideHandle"
            class="drawer-handle-wrapper"
            @touchstart.stop="onDragStart">
            <div class="drawer-handle" />
          </div>
          <div
            class="drawer-header"
            @touchstart.stop="hideHandle ? undefined : onDragStart($event)">
            <slot name="header"></slot>
          </div>
          <div
            class="drawer-body"
            @touchstart.stop="onBodyTouchStart">
            <slot></slot>
          </div>
          <div
            class="drawer-footer"
            :class="{ 'drawer-footer--filled': hideHandle }">
            <slot name="footer"></slot>
          </div>
          <button
            class="close-button modal-close-button"
            @click="closeDrawer">
            <svg>
              <use :xlink:href="`#${cancel}`" />
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import cancel from '@/assets/icons/cancel.svg'

  const props = defineProps<{
    isOpen: boolean
    fullScreen?: boolean
    hideHandle?: boolean
  }>()

  const emit = defineEmits<{ close: [] }>()
  const isMobile = window.matchMedia('(pointer: coarse)').matches

  // currentY drives sheet height: height = window.innerHeight - currentY.
  // 0 = full screen, window.innerHeight * 0.25 = partially open, window.innerHeight = hidden (height 0).
  const currentY = ref(0)
  const isDragging = ref(false)
  // Reactive mirror of window.innerHeight so the sheet height re-derives on resize /
  // orientation change (a plain window.innerHeight read in contentStyle would not).
  const windowHeight = ref(window.innerHeight)

  const partiallyOpen = () => windowHeight.value * 0.25

  function getSafeAreaTop(): number {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-inset-top')
      .trim()
    return parseInt(val) || 0
  }
  const FULL_OPEN_Y = getSafeAreaTop() + 8
  const SNAP_TO_FULL_THRESHOLD = 0.25
  const DISMISS_THRESHOLD = 0.65

  watch(
    () => props.isOpen,
    (open) => {
      if (open) {
        // Set the resting height before the sheet renders (watcher runs pre-flush, before
        // the component re-renders), so height never tweens on open — only the transform
        // slide-up animates (driven by the .slide-up-* transition classes in <style>).
        if (isMobile && !props.fullScreen) {
          currentY.value = partiallyOpen()
        } else if (props.hideHandle) {
          // Handle-less fullscreen sheet fills the whole page (no top gap).
          currentY.value = 0
        } else {
          currentY.value = FULL_OPEN_Y
        }
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    { immediate: true },
  )

  const contentStyle = computed(() => ({
    height: `${windowHeight.value - currentY.value}px`,
    // Square corners on desktop and on the handle-less fullscreen sheet (fills the page).
    borderRadius: !isMobile || props.hideHandle ? '0' : undefined,
  }))

  // Drag-to-dismiss / drag-to-expand — document-level listeners survive finger drift
  const dragStartY = ref(0)
  const snapAtDragStart = ref(0)
  // Inside the scrollable body we arbitrate between dragging the sheet and scrolling
  // the content; the handle/header always drag, so they default to 'drag'.
  const gestureMode = ref<'undecided' | 'drag' | 'scroll'>('drag')
  const DIRECTION_THRESHOLD = 6 // px before we commit drag vs scroll

  function onDragMove(e: TouchEvent) {
    const y = e.touches[0].clientY

    if (gestureMode.value === 'undecided') {
      const delta = y - dragStartY.value
      if (Math.abs(delta) < DIRECTION_THRESHOLD) return
      const atFullOpen = currentY.value <= FULL_OPEN_Y + 1
      // Below full open there's room to expand, so a body touch drags the sheet.
      // Once fully open the body only ever scrolls its content — closing is reserved
      // for the handle/header/overlay so scrolling a long list back to the top can't
      // accidentally start dismissing the sheet.
      if (!atFullOpen) {
        gestureMode.value = 'drag'
        isDragging.value = true
        dragStartY.value = y // re-anchor so the sheet doesn't jump by the threshold
        snapAtDragStart.value = currentY.value
      } else {
        // Let native scroll run: drop our non-passive touchmove handler so it no longer
        // fires (and no longer inhibits scroll) for the rest of this gesture.
        gestureMode.value = 'scroll'
        document.removeEventListener('touchmove', onDragMove)
      }
    }

    if (gestureMode.value === 'drag') {
      e.preventDefault()
      currentY.value = Math.max(FULL_OPEN_Y, snapAtDragStart.value + (y - dragStartY.value))
    }
  }

  function onDragEnd() {
    document.removeEventListener('touchmove', onDragMove)
    document.removeEventListener('touchend', onDragEnd)

    if (gestureMode.value !== 'drag') {
      gestureMode.value = 'drag' // reset for next gesture; nothing to snap on a scroll
      return
    }

    if (currentY.value < windowHeight.value * SNAP_TO_FULL_THRESHOLD) {
      currentY.value = FULL_OPEN_Y
    } else if (currentY.value > windowHeight.value * DISMISS_THRESHOLD) {
      closeDrawer()
    } else {
      currentY.value = partiallyOpen()
    }

    isDragging.value = false
  }

  function startGesture(e: TouchEvent, mode: 'drag' | 'undecided') {
    gestureMode.value = mode
    isDragging.value = mode === 'drag'
    dragStartY.value = e.touches[0].clientY
    snapAtDragStart.value = currentY.value
    document.addEventListener('touchmove', onDragMove, { passive: false })
    document.addEventListener('touchend', onDragEnd)
  }

  // Handle / header always drag the sheet; the body decides drag vs scroll on the first move.
  const onDragStart = (e: TouchEvent) => startGesture(e, 'drag')
  const onBodyTouchStart = (e: TouchEvent) => startGesture(e, 'undecided')

  // Keep the sheet sized to the live viewport. `resize` fires on web resizes and on
  // mobile orientation changes (after innerHeight settles, unlike `orientationchange`),
  // so we re-read the height and re-settle to the open snap point for the new viewport.
  function onViewportResize() {
    windowHeight.value = window.innerHeight
    if (!props.isOpen || isDragging.value) return
    // Mirror the open watcher's snap branches so behaviour is identical post-resize.
    if (isMobile && !props.fullScreen) {
      currentY.value = partiallyOpen()
    } else if (props.hideHandle) {
      currentY.value = 0
    } else {
      currentY.value = FULL_OPEN_Y
    }
  }

  function closeDrawer() {
    document.removeEventListener('touchmove', onDragMove)
    document.removeEventListener('touchend', onDragEnd)
    isDragging.value = false
    // Keep the sheet's height — the leave transition slides it down via transform
    // (collapsing height here would shrink it in place, not slide it off-screen).
    emit('close')
  }

  onMounted(() => window.addEventListener('resize', onViewportResize))

  onUnmounted(() => {
    document.removeEventListener('touchmove', onDragMove)
    document.removeEventListener('touchend', onDragEnd)
    window.removeEventListener('resize', onViewportResize)
    document.body.style.overflow = ''
  })
</script>

<style lang="scss" scoped>
  // iOS-style decel curve shared by the sheet slide, the post-drag snap, and the backdrop fade.
  $motion-timing: 0.45s cubic-bezier(0.32, 0.72, 0, 1);

  .drawer-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-end;
    z-index: 1000;
  }

  .drawer-content {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    max-width: $breakpoint-sm;
    background: $clr-white;
    border-radius: rem(20) rem(20) 0 0;
    overflow: hidden;
    border: 1px solid $clr-gray-100;
    border-bottom: none;
    box-shadow: 0 0 rem(3) rgba(42, 52, 122, 0.2);
    will-change: transform;
    // transform animates the entrance/exit slide (composited); height animates the
    // post-drag snap.
    transition:
      transform $motion-timing,
      height $motion-timing;
  }

  // While dragging, the sheet tracks the finger 1:1 — no transition.
  .drawer-content--dragging {
    transition: none;
  }

  // Handle-less fullscreen sheet fills to y=0, so push the title/X clear of the
  // status bar / notch (the FULL_OPEN_Y offset that normally did this is dropped).
  .drawer-content--no-handle {
    padding-top: var(--safe-area-inset-top);

    .close-button {
      top: calc(rem(16) + var(--safe-area-inset-top));
    }
  }

  .drawer-handle-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: rem(16) 0 rem(8);
    flex-shrink: 0;
    cursor: grab;
    touch-action: none;
  }

  .drawer-handle {
    width: rem(57);
    height: rem(8);
    border-radius: rem(999);
    background: #d9d9d9;
    pointer-events: none;
  }

  .drawer-header {
    flex-shrink: 0;
    display: flex;
    justify-content: center;

    padding-right: 32px;
    padding-left: 16px;
    font-weight: 600;
  }

  // Positioning only; the pill background + icon sizing/colour come from the
  // global `.modal-close-button` class (styles.scss), matching Norway.Booking.S3.
  .close-button {
    position: absolute;
    top: rem(16);
    right: rem(16);
    z-index: 2;
  }

  .drawer-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    padding-right: 16px;
    padding-left: 16px;
    margin-top: rem(16);

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;
    scrollbar-width: none;
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .drawer-footer {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    background-color: $clr-white;
    padding-bottom: var(--safe-area-inset-bottom);

    // Match the footer (incl. its safe-area-bottom strip) to the action bar above
    // it so there's no white seam under the button on the handle-less sheet.
    &--filled {
      background-color: $clr-background;
    }

    :deep(> button) {
      border-color: $clr-primary-50;
    }
  }

  // The dark backdrop fades on the overlay; this transition also doubles as Vue's
  // <transition> timing anchor, keeping the sheet mounted long enough to finish its
  // slide on leave, so its duration is matched to the sheet's.
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: background-color $motion-timing;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    background-color: rgba(0, 0, 0, 0);
  }

  // Slide the sheet up on enter / down on leave. Applied to the leaving DOM node by Vue's
  // transition system (reactive style bindings don't reach a leaving node), animated by
  // .drawer-content's own transform transition.
  .slide-up-enter-from .drawer-content,
  .slide-up-leave-to .drawer-content {
    transform: translateY(100%);
  }

  @media (prefers-reduced-motion: reduce) {
    .drawer-content,
    .slide-up-enter-active,
    .slide-up-leave-active {
      transition: none !important;
    }
  }
</style>
